import {
  CUSTOMER_FAVOURITE_ENTITY_TYPES,
  CUSTOMER_FAVOURITES_LOCALES,
  CUSTOMER_FAVOURITES_PAGE_SIZE_MAX,
  CUSTOMER_FAVOURITES_PARTIAL_FIELDS,
  CUSTOMER_FAVOURITES_SCHEMA_VERSION,
  type CustomerFavouriteActionCapability,
  type CustomerFavouriteLocationAvailability,
  type CustomerFavouriteLocationCatalog,
  type CustomerFavouriteLocationCatalogEntry,
  type CustomerFavouriteLocationBookingCapability,
  type CustomerFavouriteLocationImage,
  type CustomerFavouriteLocationItem,
  type CustomerFavouriteLocationJoinResult,
  type CustomerFavouriteLocationReferenceV1,
  type CustomerFavouriteLocationRejectedReference,
  type CustomerFavouriteLocationViewCapability,
  type CustomerFavouritesCapabilities,
  type CustomerFavouritesDisplayableProviderResult,
  type CustomerFavouritesOwner,
  type CustomerFavouritesPageProviderResult,
  type CustomerFavouritesPartialField,
  type CustomerFavouritesProvider,
  type CustomerFavouritesProviderErrorReason,
  type CustomerFavouritesProviderResult,
  type CustomerFavouritesProviderResultIdentity,
  type CustomerFavouritesProviderResultExpectation,
  type CustomerFavouritesReadRequest,
  type CustomerFavouritesSnapshotV1,
} from "./customerFavouritesContracts";

type UnknownRecord = Record<string, unknown>;

const IDENTIFIER_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]*$/u;
const INTERNAL_ORIGIN = "https://volera.local";
const MAX_CATALOG_LOCATIONS = 1_000;
const MAX_FAVOURITES_PER_SNAPSHOT = 1_000;

function isRecord(value: unknown): value is UnknownRecord {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasExactKeys(value: UnknownRecord, keys: readonly string[]) {
  const actualKeys = Object.keys(value);
  return (
    actualKeys.length === keys.length &&
    actualKeys.every((key) => keys.includes(key))
  );
}

export function isCustomerFavouritesIdentifier(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= 160 &&
    IDENTIFIER_PATTERN.test(value)
  );
}

function isCursor(value: unknown): value is string | null {
  return value === null || isCustomerFavouritesIdentifier(value);
}

function isEpoch(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0
  );
}

function isNonNegativeInteger(
  value: unknown,
  maximum = 1_000_000_000,
): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= maximum
  );
}

function isText(value: unknown, maximum = 2_000): value is string {
  return (
    typeof value === "string" &&
    value === value.trim() &&
    value.length > 0 &&
    value.length <= maximum &&
    !/[\u0000-\u001F\u007F]/u.test(value)
  );
}

function isNullableText(
  value: unknown,
  maximum = 2_000,
): value is string | null {
  return value === null || isText(value, maximum);
}

function isSafeInternalPath(value: unknown): value is string {
  if (
    typeof value !== "string" ||
    value !== value.trim() ||
    value.length === 0 ||
    value.length > 2_000 ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    value.includes("\\") ||
    /[\u0000-\u001F\u007F]/u.test(value)
  ) {
    return false;
  }

  try {
    const parsed = new URL(value, INTERNAL_ORIGIN);
    return (
      parsed.origin === INTERNAL_ORIGIN &&
      !/%2f|%5c/iu.test(parsed.pathname) &&
      `${parsed.pathname}${parsed.search}${parsed.hash}` === value
    );
  } catch {
    return false;
  }
}

function isImageUrl(value: unknown): value is string {
  if (
    typeof value !== "string" ||
    value !== value.trim() ||
    value.length === 0 ||
    value.length > 2_000 ||
    /[\u0000-\u001F\u007F]/u.test(value)
  ) {
    return false;
  }

  if (value.startsWith("/")) {
    return isSafeInternalPath(value);
  }

  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
}

function cloneAndFreeze<T>(value: T): T {
  if (Array.isArray(value)) {
    return Object.freeze(value.map((entry) => cloneAndFreeze(entry))) as T;
  }

  if (isRecord(value)) {
    const clone: UnknownRecord = {};
    for (const [key, entry] of Object.entries(value)) {
      clone[key] = cloneAndFreeze(entry);
    }
    return Object.freeze(clone) as T;
  }

  return value;
}

function canonicalDataTreeIsSafe(
  value: unknown,
  seen: WeakSet<object>,
): boolean {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return true;
  }

  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  if (typeof value !== "object" || seen.has(value)) {
    return false;
  }
  seen.add(value);

  const prototype = Object.getPrototypeOf(value);
  const descriptors = Object.getOwnPropertyDescriptors(value);
  const ownKeys = Reflect.ownKeys(descriptors);

  if (Array.isArray(value)) {
    if (prototype !== Array.prototype) {
      return false;
    }

    const lengthDescriptor = descriptors.length;
    if (
      !lengthDescriptor ||
      !("value" in lengthDescriptor) ||
      lengthDescriptor.enumerable ||
      !Number.isSafeInteger(lengthDescriptor.value) ||
      lengthDescriptor.value < 0
    ) {
      return false;
    }

    const length = lengthDescriptor.value as number;
    const elementKeys = ownKeys.filter((key) => key !== "length");
    if (elementKeys.length !== length) {
      return false;
    }

    return elementKeys.every((key) => {
      if (
        typeof key !== "string" ||
        !/^(0|[1-9][0-9]*)$/u.test(key) ||
        Number(key) >= length
      ) {
        return false;
      }

      const descriptor = descriptors[key];
      return Boolean(
        descriptor &&
          descriptor.enumerable &&
          "value" in descriptor &&
          canonicalDataTreeIsSafe(descriptor.value, seen),
      );
    });
  }

  if (prototype !== Object.prototype && prototype !== null) {
    return false;
  }

  return ownKeys.every((key) => {
    if (typeof key !== "string") {
      return false;
    }

    const descriptor = descriptors[key];
    return Boolean(
      descriptor &&
        descriptor.enumerable &&
        "value" in descriptor &&
        canonicalDataTreeIsSafe(descriptor.value, seen),
    );
  });
}

/**
 * Captures an untrusted provider payload before any semantic field access.
 * Provider results are canonical data trees only: accessors, symbols,
 * non-enumerable properties, custom prototypes, shared/cyclic references,
 * Proxies, and structured-clone failures all reject the complete payload.
 */
function detachCanonicalProviderResult(value: unknown): unknown | null {
  try {
    if (!canonicalDataTreeIsSafe(value, new WeakSet<object>())) {
      return null;
    }

    return structuredClone(value);
  } catch {
    return null;
  }
}

function validateActionCapability(
  value: unknown,
): CustomerFavouriteActionCapability | null {
  if (!isRecord(value) || typeof value.status !== "string") {
    return null;
  }

  if (value.status === "allowed" && hasExactKeys(value, ["status"])) {
    return Object.freeze({ status: "allowed" as const });
  }

  if (
    value.status === "unavailable" &&
    hasExactKeys(value, ["status", "reason"]) &&
    value.reason === "provider-disabled"
  ) {
    return Object.freeze({
      status: "unavailable" as const,
      reason: "provider-disabled" as const,
    });
  }

  return null;
}

function validateCapabilities(
  value: unknown,
): CustomerFavouritesCapabilities | null {
  if (!isRecord(value) || !hasExactKeys(value, ["add", "remove"])) {
    return null;
  }

  const add = validateActionCapability(value.add);
  const remove = validateActionCapability(value.remove);
  return add && remove ? Object.freeze({ add, remove }) : null;
}

function validateReference(
  value: unknown,
): CustomerFavouriteLocationReferenceV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "favouriteId",
      "entityType",
      "locationId",
      "savedAtEpochMs",
    ]) ||
    !isCustomerFavouritesIdentifier(value.favouriteId) ||
    !(CUSTOMER_FAVOURITE_ENTITY_TYPES as readonly unknown[]).includes(
      value.entityType,
    ) ||
    value.entityType !== "location" ||
    !isCustomerFavouritesIdentifier(value.locationId) ||
    !isEpoch(value.savedAtEpochMs)
  ) {
    return null;
  }

  return Object.freeze({
    favouriteId: value.favouriteId,
    entityType: "location" as const,
    locationId: value.locationId,
    savedAtEpochMs: value.savedAtEpochMs,
  });
}

function validateImage(value: unknown): CustomerFavouriteLocationImage | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["imageId", "url", "alt"]) ||
    !isCustomerFavouritesIdentifier(value.imageId) ||
    !isImageUrl(value.url) ||
    typeof value.alt !== "string" ||
    value.alt.length > 240 ||
    /[\u0000-\u001F\u007F]/u.test(value.alt)
  ) {
    return null;
  }

  return Object.freeze({
    imageId: value.imageId,
    url: value.url,
    alt: value.alt,
  });
}

function validateBadge(value: unknown) {
  if (value === null) {
    return null;
  }

  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["badgeId", "label"]) ||
    !isCustomerFavouritesIdentifier(value.badgeId) ||
    !isText(value.label, 80)
  ) {
    return undefined;
  }

  return Object.freeze({ badgeId: value.badgeId, label: value.label });
}

function validateCatalogEntry(
  value: unknown,
  displayRevision: string,
): CustomerFavouriteLocationCatalogEntry | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "locationId",
      "displayRevision",
      "businessId",
      "businessName",
      "locationName",
      "publicationState",
      "businessStatus",
      "bookingStatus",
      "images",
      "rating",
      "reviewCount",
      "address",
      "category",
      "badge",
      "destination",
    ]) ||
    !isCustomerFavouritesIdentifier(value.locationId) ||
    value.displayRevision !== displayRevision ||
    !isCustomerFavouritesIdentifier(value.businessId) ||
    !isText(value.businessName, 240) ||
    !isText(value.locationName, 240) ||
    (value.publicationState !== "public" &&
      value.publicationState !== "hidden" &&
      value.publicationState !== "withdrawn") ||
    (value.businessStatus !== "open" && value.businessStatus !== "closed") ||
    (value.bookingStatus !== "available" &&
      value.bookingStatus !== "unavailable" &&
      value.bookingStatus !== "unknown") ||
    !Array.isArray(value.images) ||
    value.images.length > 8 ||
    (value.rating !== null &&
      (typeof value.rating !== "number" ||
        !Number.isFinite(value.rating) ||
        value.rating < 0 ||
        value.rating > 5)) ||
    (value.reviewCount !== null &&
      !isNonNegativeInteger(value.reviewCount)) ||
    !isNullableText(value.address, 500) ||
    !isNullableText(value.category, 160)
  ) {
    return null;
  }

  const images = value.images.map(validateImage);
  if (
    images.some((image) => image === null) ||
    new Set(images.map((image) => image?.imageId)).size !== images.length
  ) {
    return null;
  }

  const badge = validateBadge(value.badge);
  if (badge === undefined) {
    return null;
  }

  if (
    value.businessStatus === "closed" &&
    value.bookingStatus !== "unavailable"
  ) {
    return null;
  }

  if (value.publicationState === "public") {
    if (value.destination !== null && !isSafeInternalPath(value.destination)) {
      return null;
    }
  } else if (
    value.bookingStatus !== "unavailable" ||
    value.destination !== null
  ) {
    return null;
  }

  return cloneAndFreeze({
    locationId: value.locationId,
    displayRevision,
    businessId: value.businessId,
    businessName: value.businessName,
    locationName: value.locationName,
    publicationState: value.publicationState,
    businessStatus: value.businessStatus,
    bookingStatus: value.bookingStatus,
    images: images as CustomerFavouriteLocationImage[],
    rating: value.rating,
    reviewCount: value.reviewCount,
    address: value.address,
    category: value.category,
    badge,
    destination: value.destination,
  }) as CustomerFavouriteLocationCatalogEntry;
}

/**
 * Validates and detaches the current location-presentation catalog. Duplicate
 * location IDs reject the complete catalog so callers cannot join against an
 * ambiguous presentation owner.
 */
export function validateCustomerFavouriteLocationCatalog(
  value: unknown,
): CustomerFavouriteLocationCatalog | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "schemaVersion",
      "kind",
      "displayRevision",
      "locations",
    ]) ||
    value.schemaVersion !== CUSTOMER_FAVOURITES_SCHEMA_VERSION ||
    value.kind !== "customer-favourite-location-catalog" ||
    !isCustomerFavouritesIdentifier(value.displayRevision) ||
    !Array.isArray(value.locations) ||
    value.locations.length > MAX_CATALOG_LOCATIONS
  ) {
    return null;
  }

  const locations = value.locations.map((entry) =>
    validateCatalogEntry(entry, value.displayRevision as string),
  );
  if (
    locations.some((entry) => entry === null) ||
    new Set(locations.map((entry) => entry?.locationId)).size !==
      locations.length
  ) {
    return null;
  }

  return cloneAndFreeze({
    schemaVersion: CUSTOMER_FAVOURITES_SCHEMA_VERSION,
    kind: "customer-favourite-location-catalog" as const,
    displayRevision: value.displayRevision,
    locations: locations as CustomerFavouriteLocationCatalogEntry[],
  });
}

/**
 * Treats every provider-bound read request as untrusted, even when it was
 * assembled by a local caller. The accepted request is an exact, detached,
 * deeply frozen value, including its presentation catalog, so a provider
 * cannot rewrite the authority that will later validate its response.
 */
export function validateCustomerFavouritesReadRequest(
  value: unknown,
): CustomerFavouritesReadRequest | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "customerAccountId",
      "locale",
      "catalog",
      "displayRevision",
      "snapshotRevision",
      "cursor",
      "pageSize",
      "requestKey",
    ]) ||
    !isCustomerFavouritesIdentifier(value.customerAccountId) ||
    !(CUSTOMER_FAVOURITES_LOCALES as readonly unknown[]).includes(
      value.locale,
    ) ||
    !isCustomerFavouritesIdentifier(value.displayRevision) ||
    (value.snapshotRevision !== null &&
      !isCustomerFavouritesIdentifier(value.snapshotRevision)) ||
    !isCursor(value.cursor) ||
    typeof value.pageSize !== "number" ||
    !Number.isSafeInteger(value.pageSize) ||
    value.pageSize < 1 ||
    value.pageSize > CUSTOMER_FAVOURITES_PAGE_SIZE_MAX ||
    !isCustomerFavouritesIdentifier(value.requestKey)
  ) {
    return null;
  }

  const catalog = validateCustomerFavouriteLocationCatalog(value.catalog);
  if (!catalog || catalog.displayRevision !== value.displayRevision) {
    return null;
  }

  return cloneAndFreeze({
    customerAccountId: value.customerAccountId,
    locale: value.locale,
    catalog,
    displayRevision: value.displayRevision,
    snapshotRevision: value.snapshotRevision,
    cursor: value.cursor,
    pageSize: value.pageSize,
    requestKey: value.requestKey,
  }) as CustomerFavouritesReadRequest;
}

function validateAvailability(
  value: unknown,
  businessStatus: "open" | "closed",
  bookingStatus: "available" | "unavailable" | "unknown",
): CustomerFavouriteLocationAvailability | null {
  if (!isRecord(value) || typeof value.status !== "string") {
    return null;
  }

  if (
    value.status === "unknown" &&
    hasExactKeys(value, ["status", "reason"]) &&
    value.reason === "availability-unverified" &&
    businessStatus === "open" &&
    bookingStatus === "unknown"
  ) {
    return Object.freeze({
      status: "unknown" as const,
      reason: "availability-unverified" as const,
    });
  }

  if (
    value.status === "available" &&
    hasExactKeys(value, ["status", "reason"]) &&
    value.reason === null &&
    businessStatus === "open" &&
    bookingStatus === "available"
  ) {
    return Object.freeze({ status: "available" as const, reason: null });
  }

  const expectedReason =
    businessStatus === "closed" ? "business-closed" : "booking-unavailable";
  if (
    value.status === "unavailable" &&
    hasExactKeys(value, ["status", "reason"]) &&
    value.reason === expectedReason &&
    (businessStatus === "closed" || bookingStatus === "unavailable")
  ) {
    return Object.freeze({
      status: "unavailable" as const,
      reason: expectedReason,
    });
  }

  return null;
}

function validateViewCapability(
  value: unknown,
  destination: string | null,
): CustomerFavouriteLocationViewCapability | null {
  if (!isRecord(value) || typeof value.status !== "string") {
    return null;
  }

  if (
    value.status === "allowed" &&
    hasExactKeys(value, ["status", "destination"]) &&
    destination !== null &&
    value.destination === destination &&
    isSafeInternalPath(value.destination)
  ) {
    return Object.freeze({
      status: "allowed" as const,
      destination,
    });
  }

  if (
    value.status === "unavailable" &&
    hasExactKeys(value, ["status", "reason"]) &&
    value.reason === "destination-unavailable" &&
    destination === null
  ) {
    return Object.freeze({
      status: "unavailable" as const,
      reason: "destination-unavailable" as const,
    });
  }

  return null;
}

function validateBookingCapability(
  value: unknown,
  availability: CustomerFavouriteLocationAvailability,
): CustomerFavouriteLocationBookingCapability | null {
  if (!isRecord(value) || typeof value.status !== "string") {
    return null;
  }

  if (
    value.status === "allowed" &&
    hasExactKeys(value, ["status"]) &&
    availability.status === "available"
  ) {
    return Object.freeze({ status: "allowed" as const });
  }

  if (
    value.status === "unknown" &&
    hasExactKeys(value, ["status", "reason"]) &&
    value.reason === "availability-unverified" &&
    availability.status === "unknown"
  ) {
    return Object.freeze({
      status: "unknown" as const,
      reason: "availability-unverified" as const,
    });
  }

  if (
    value.status === "unavailable" &&
    hasExactKeys(value, ["status", "reason"]) &&
    availability.status === "unavailable" &&
    value.reason === availability.reason
  ) {
    return Object.freeze({
      status: "unavailable" as const,
      reason: availability.reason,
    });
  }

  return null;
}

function validateLocationItem(
  value: unknown,
  displayRevision: string,
): CustomerFavouriteLocationItem | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "favouriteId",
      "entityType",
      "locationId",
      "savedAtEpochMs",
      "displayRevision",
      "businessId",
      "businessName",
      "locationName",
      "publicationState",
      "businessStatus",
      "bookingStatus",
      "images",
      "rating",
      "reviewCount",
      "address",
      "category",
      "badge",
      "destination",
      "availability",
      "viewCapability",
      "bookingCapability",
      "capabilities",
    ]) ||
    !isCustomerFavouritesIdentifier(value.favouriteId) ||
    value.entityType !== "location" ||
    !isCustomerFavouritesIdentifier(value.locationId) ||
    !isEpoch(value.savedAtEpochMs) ||
    value.displayRevision !== displayRevision ||
    !isCustomerFavouritesIdentifier(value.businessId) ||
    !isText(value.businessName, 240) ||
    !isText(value.locationName, 240) ||
    value.publicationState !== "public" ||
    (value.businessStatus !== "open" && value.businessStatus !== "closed") ||
    (value.bookingStatus !== "available" &&
      value.bookingStatus !== "unavailable" &&
      value.bookingStatus !== "unknown") ||
    !Array.isArray(value.images) ||
    value.images.length > 8 ||
    (value.rating !== null &&
      (typeof value.rating !== "number" ||
        !Number.isFinite(value.rating) ||
        value.rating < 0 ||
        value.rating > 5)) ||
    (value.reviewCount !== null &&
      !isNonNegativeInteger(value.reviewCount)) ||
    !isNullableText(value.address, 500) ||
    !isNullableText(value.category, 160) ||
    (value.destination !== null && !isSafeInternalPath(value.destination))
  ) {
    return null;
  }

  const images = value.images.map(validateImage);
  const badge = validateBadge(value.badge);
  const capabilities = validateCapabilities(value.capabilities);
  const availability = validateAvailability(
    value.availability,
    value.businessStatus,
    value.bookingStatus,
  );
  const viewCapability = validateViewCapability(
    value.viewCapability,
    value.destination,
  );
  const bookingCapability = availability
    ? validateBookingCapability(value.bookingCapability, availability)
    : null;
  if (
    images.some((image) => image === null) ||
    new Set(images.map((image) => image?.imageId)).size !== images.length ||
    badge === undefined ||
    !capabilities ||
    !availability ||
    !viewCapability ||
    !bookingCapability
  ) {
    return null;
  }

  return cloneAndFreeze({
    favouriteId: value.favouriteId,
    entityType: "location" as const,
    locationId: value.locationId,
    savedAtEpochMs: value.savedAtEpochMs,
    displayRevision,
    businessId: value.businessId,
    businessName: value.businessName,
    locationName: value.locationName,
    publicationState: "public" as const,
    businessStatus: value.businessStatus,
    bookingStatus: value.bookingStatus,
    images: images as CustomerFavouriteLocationImage[],
    rating: value.rating,
    reviewCount: value.reviewCount,
    address: value.address,
    category: value.category,
    badge,
    destination: value.destination,
    availability,
    viewCapability,
    bookingCapability,
    capabilities,
  }) as CustomerFavouriteLocationItem;
}

function validateRejectedReference(
  value: unknown,
): CustomerFavouriteLocationRejectedReference | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["reference", "reason"]) ||
    (value.reason !== "duplicate-favourite-id" &&
      value.reason !== "duplicate-location-id" &&
      value.reason !== "unknown-location" &&
      value.reason !== "hidden-location" &&
      value.reason !== "withdrawn-location")
  ) {
    return null;
  }

  const reference = validateReference(value.reference);
  return reference
    ? Object.freeze({ reference, reason: value.reason })
    : null;
}

export function validateCustomerFavouritesSnapshot(
  value: unknown,
): CustomerFavouritesSnapshotV1 | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "schemaVersion",
      "kind",
      "customerAccountId",
      "providerId",
      "providerRevision",
      "snapshotId",
      "snapshotRevision",
      "displayRevision",
      "generatedAtEpochMs",
      "capabilities",
      "favourites",
      "items",
      "rejected",
      "pagination",
    ]) ||
    value.schemaVersion !== CUSTOMER_FAVOURITES_SCHEMA_VERSION ||
    value.kind !== "customer-favourites-snapshot" ||
    !isCustomerFavouritesIdentifier(value.customerAccountId) ||
    !isCustomerFavouritesIdentifier(value.providerId) ||
    !isCustomerFavouritesIdentifier(value.providerRevision) ||
    !isCustomerFavouritesIdentifier(value.snapshotId) ||
    !isCustomerFavouritesIdentifier(value.snapshotRevision) ||
    !isCustomerFavouritesIdentifier(value.displayRevision) ||
    !isEpoch(value.generatedAtEpochMs) ||
    !Array.isArray(value.favourites) ||
    value.favourites.length > MAX_FAVOURITES_PER_SNAPSHOT ||
    !Array.isArray(value.items) ||
    value.items.length > MAX_FAVOURITES_PER_SNAPSHOT ||
    !Array.isArray(value.rejected) ||
    value.rejected.length > MAX_FAVOURITES_PER_SNAPSHOT ||
    !isRecord(value.pagination) ||
    !hasExactKeys(value.pagination, [
      "cursor",
      "nextCursor",
      "pageSize",
      "returnedCount",
      "totalCount",
    ])
  ) {
    return null;
  }

  const capabilities = validateCapabilities(value.capabilities);
  const favourites = value.favourites.map(validateReference);
  const items = value.items.map((item) =>
    validateLocationItem(item, value.displayRevision as string),
  );
  const rejected = value.rejected.map(validateRejectedReference);
  const pagination = value.pagination;
  if (
    !capabilities ||
    favourites.some((reference) => reference === null) ||
    items.some((item) => item === null) ||
    rejected.some((entry) => entry === null) ||
    !isCursor(pagination.cursor) ||
    !isCursor(pagination.nextCursor) ||
    (pagination.cursor !== null &&
      pagination.cursor === pagination.nextCursor) ||
    typeof pagination.pageSize !== "number" ||
    !Number.isSafeInteger(pagination.pageSize) ||
    pagination.pageSize < 1 ||
    pagination.pageSize > CUSTOMER_FAVOURITES_PAGE_SIZE_MAX ||
    !isNonNegativeInteger(pagination.returnedCount) ||
    !isNonNegativeInteger(pagination.totalCount) ||
    pagination.returnedCount !== favourites.length ||
    pagination.returnedCount > pagination.pageSize ||
    pagination.totalCount < pagination.returnedCount ||
    new Set(items.map((item) => item?.favouriteId)).size !== items.length ||
    new Set(items.map((item) => item?.locationId)).size !== items.length
  ) {
    return null;
  }

  return cloneAndFreeze({
    schemaVersion: CUSTOMER_FAVOURITES_SCHEMA_VERSION,
    kind: "customer-favourites-snapshot" as const,
    customerAccountId: value.customerAccountId,
    providerId: value.providerId,
    providerRevision: value.providerRevision,
    snapshotId: value.snapshotId,
    snapshotRevision: value.snapshotRevision,
    displayRevision: value.displayRevision,
    generatedAtEpochMs: value.generatedAtEpochMs,
    capabilities,
    favourites: favourites as CustomerFavouriteLocationReferenceV1[],
    items: items as CustomerFavouriteLocationItem[],
    rejected: rejected as CustomerFavouriteLocationRejectedReference[],
    pagination: {
      cursor: pagination.cursor,
      nextCursor: pagination.nextCursor,
      pageSize: pagination.pageSize,
      returnedCount: pagination.returnedCount,
      totalCount: pagination.totalCount,
    },
  });
}

export function getCustomerFavouritesOwner(
  snapshot: CustomerFavouritesSnapshotV1,
): CustomerFavouritesOwner {
  return Object.freeze({
    customerAccountId: snapshot.customerAccountId,
    providerId: snapshot.providerId,
    providerRevision: snapshot.providerRevision,
    snapshotId: snapshot.snapshotId,
    snapshotRevision: snapshot.snapshotRevision,
    displayRevision: snapshot.displayRevision,
  });
}

/**
 * Rejoins provider-owned saved references to the current display catalog.
 * Provider ordering is retained, the first exact favourite/location identity
 * wins, and unknown or non-public locations never become visible/actionable.
 */
export function joinCustomerFavouriteLocations(
  snapshotValue: unknown,
  catalogValue: unknown,
): CustomerFavouriteLocationJoinResult {
  const snapshot = validateCustomerFavouritesSnapshot(snapshotValue);
  if (!snapshot) {
    return Object.freeze({ valid: false as const, reason: "snapshot-invalid" });
  }

  const catalog = validateCustomerFavouriteLocationCatalog(catalogValue);
  if (!catalog) {
    return Object.freeze({ valid: false as const, reason: "catalog-invalid" });
  }

  if (snapshot.displayRevision !== catalog.displayRevision) {
    return Object.freeze({
      valid: false as const,
      reason: "display-revision-mismatch" as const,
    });
  }

  const locationById = new Map(
    catalog.locations.map((location) => [location.locationId, location]),
  );
  const seenFavouriteIds = new Set<string>();
  const seenLocationIds = new Set<string>();
  const items: CustomerFavouriteLocationItem[] = [];
  const rejected: CustomerFavouriteLocationRejectedReference[] = [];

  for (const reference of snapshot.favourites) {
    let rejectionReason:
      | CustomerFavouriteLocationRejectedReference["reason"]
      | null = null;

    if (seenFavouriteIds.has(reference.favouriteId)) {
      rejectionReason = "duplicate-favourite-id";
    } else if (seenLocationIds.has(reference.locationId)) {
      rejectionReason = "duplicate-location-id";
    }

    seenFavouriteIds.add(reference.favouriteId);
    seenLocationIds.add(reference.locationId);

    const location = locationById.get(reference.locationId);
    if (!rejectionReason && !location) {
      rejectionReason = "unknown-location";
    } else if (!rejectionReason && location?.publicationState === "hidden") {
      rejectionReason = "hidden-location";
    } else if (
      !rejectionReason &&
      location?.publicationState === "withdrawn"
    ) {
      rejectionReason = "withdrawn-location";
    }

    if (rejectionReason) {
      rejected.push(Object.freeze({ reference, reason: rejectionReason }));
      continue;
    }

    if (!location || location.publicationState !== "public") {
      continue;
    }

    const availability: CustomerFavouriteLocationAvailability =
      location.businessStatus === "closed"
        ? Object.freeze({
            status: "unavailable" as const,
            reason: "business-closed" as const,
          })
        : location.bookingStatus === "unavailable"
          ? Object.freeze({
              status: "unavailable" as const,
              reason: "booking-unavailable" as const,
            })
          : location.bookingStatus === "unknown"
            ? Object.freeze({
                status: "unknown" as const,
                reason: "availability-unverified" as const,
              })
            : Object.freeze({ status: "available" as const, reason: null });
    const viewCapability: CustomerFavouriteLocationViewCapability =
      location.destination === null
        ? Object.freeze({
            status: "unavailable" as const,
            reason: "destination-unavailable" as const,
          })
        : Object.freeze({
            status: "allowed" as const,
            destination: location.destination,
          });
    const bookingCapability: CustomerFavouriteLocationBookingCapability =
      availability.status === "available"
        ? Object.freeze({ status: "allowed" as const })
        : availability.status === "unknown"
          ? Object.freeze({
              status: "unknown" as const,
              reason: "availability-unverified" as const,
            })
          : Object.freeze({
              status: "unavailable" as const,
              reason: availability.reason,
            });

    items.push(
      cloneAndFreeze({
        favouriteId: reference.favouriteId,
        entityType: "location" as const,
        locationId: reference.locationId,
        savedAtEpochMs: reference.savedAtEpochMs,
        displayRevision: catalog.displayRevision,
        businessId: location.businessId,
        businessName: location.businessName,
        locationName: location.locationName,
        publicationState: "public" as const,
        businessStatus: location.businessStatus,
        bookingStatus: location.bookingStatus,
        images: location.images,
        rating: location.rating,
        reviewCount: location.reviewCount,
        address: location.address,
        category: location.category,
        badge: location.badge,
        destination: location.destination,
        availability,
        viewCapability,
        bookingCapability,
        capabilities: snapshot.capabilities,
      }),
    );
  }

  return cloneAndFreeze({
    valid: true as const,
    owner: getCustomerFavouritesOwner(snapshot),
    items,
    rejected,
  });
}

const RESULT_IDENTITY_KEYS = [
  "providerId",
  "providerRevision",
  "requestKey",
  "customerAccountId",
  "locale",
  "displayRevision",
  "requestedSnapshotRevision",
  "cursor",
  "pageSize",
] as const;

function providerIdentityMatches(
  value: UnknownRecord,
  expectation: CustomerFavouritesProviderResultExpectation,
) {
  return (
    value.providerId === expectation.providerId &&
    value.providerRevision === expectation.providerRevision &&
    value.requestKey === expectation.requestKey &&
    value.customerAccountId === expectation.customerAccountId &&
    value.locale === expectation.locale &&
    value.displayRevision === expectation.displayRevision &&
    value.requestedSnapshotRevision === expectation.snapshotRevision &&
    value.cursor === expectation.cursor &&
    value.pageSize === expectation.pageSize &&
    isCustomerFavouritesIdentifier(value.providerId) &&
    isCustomerFavouritesIdentifier(value.providerRevision) &&
    isCustomerFavouritesIdentifier(value.requestKey) &&
    isCustomerFavouritesIdentifier(value.customerAccountId) &&
    (CUSTOMER_FAVOURITES_LOCALES as readonly unknown[]).includes(
      value.locale,
    ) &&
    isCustomerFavouritesIdentifier(value.displayRevision) &&
    (value.requestedSnapshotRevision === null ||
      isCustomerFavouritesIdentifier(value.requestedSnapshotRevision)) &&
    isCursor(value.cursor) &&
    typeof value.pageSize === "number" &&
    Number.isSafeInteger(value.pageSize) &&
    value.pageSize >= 1 &&
    value.pageSize <= CUSTOMER_FAVOURITES_PAGE_SIZE_MAX
  );
}

function snapshotRevisionMatchesExpectation(
  snapshotRevision: string,
  expectation: CustomerFavouritesProviderResultExpectation,
) {
  return (
    expectation.snapshotRevision === null ||
    snapshotRevision === expectation.snapshotRevision
  );
}

function sameCanonicalValue(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function resultIdentityFromExpectation(
  expectation: CustomerFavouritesProviderResultExpectation,
): CustomerFavouritesProviderResultIdentity {
  return Object.freeze({
    providerId: expectation.providerId,
    providerRevision: expectation.providerRevision,
    requestKey: expectation.requestKey,
    customerAccountId: expectation.customerAccountId,
    locale: expectation.locale,
    displayRevision: expectation.displayRevision,
    requestedSnapshotRevision: expectation.snapshotRevision,
    cursor: expectation.cursor,
    pageSize: expectation.pageSize,
  });
}

function validateCanonicalResultSnapshot(
  value: unknown,
  expectation: CustomerFavouritesProviderResultExpectation,
  catalog: CustomerFavouriteLocationCatalog,
) {
  const snapshot = validateCustomerFavouritesSnapshot(value);
  if (
    !snapshot ||
    snapshot.providerId !== expectation.providerId ||
    snapshot.providerRevision !== expectation.providerRevision ||
    snapshot.customerAccountId !== expectation.customerAccountId ||
    snapshot.displayRevision !== expectation.displayRevision ||
    !snapshotRevisionMatchesExpectation(snapshot.snapshotRevision, expectation) ||
    snapshot.pagination.cursor !== expectation.cursor ||
    snapshot.pagination.pageSize !== expectation.pageSize
  ) {
    return null;
  }

  const joined = joinCustomerFavouriteLocations(snapshot, catalog);
  return joined.valid &&
    sameCanonicalValue(snapshot.items, joined.items) &&
    sameCanonicalValue(snapshot.rejected, joined.rejected)
    ? snapshot
    : null;
}

function hasReadOnlyCapabilities(snapshot: CustomerFavouritesSnapshotV1) {
  return (
    snapshot.capabilities.add.status === "unavailable" &&
    snapshot.capabilities.remove.status === "unavailable"
  );
}

/**
 * Treats a provider result and its display payload as untrusted. An accepted
 * displayable result is detached, deeply frozen, request-exact, and proven to
 * be the canonical join of the provider references and current catalog.
 */
export function validateCustomerFavouritesProviderResult(
  value: unknown,
  expectation: CustomerFavouritesProviderResultExpectation,
):
  | { valid: true; result: CustomerFavouritesProviderResult }
  | { valid: false; reason: "provider-response-invalid" } {
  const detached = detachCanonicalProviderResult(value);
  if (!isRecord(detached)) {
    return Object.freeze({
      valid: false as const,
      reason: "provider-response-invalid" as const,
    });
  }

  return validateDetachedCustomerFavouritesProviderResult(
    detached,
    expectation,
  );
}

function validateDetachedCustomerFavouritesProviderResult(
  value: UnknownRecord,
  expectation: CustomerFavouritesProviderResultExpectation,
):
  | { valid: true; result: CustomerFavouritesProviderResult }
  | { valid: false; reason: "provider-response-invalid" } {
  const invalid = Object.freeze({
    valid: false as const,
    reason: "provider-response-invalid" as const,
  });
  const catalog = validateCustomerFavouriteLocationCatalog(expectation.catalog);

  if (
    !catalog ||
    catalog.displayRevision !== expectation.displayRevision ||
    typeof value.status !== "string" ||
    !providerIdentityMatches(value, expectation)
  ) {
    return invalid;
  }

  if (value.status === "ready" || value.status === "empty") {
    if (
      !hasExactKeys(value, ["status", ...RESULT_IDENTITY_KEYS, "snapshot"])
    ) {
      return invalid;
    }

    const snapshot = validateCanonicalResultSnapshot(
      value.snapshot,
      expectation,
      catalog,
    );
    if (
      !snapshot ||
      (value.status === "ready" && snapshot.items.length === 0) ||
      (value.status === "empty" && snapshot.items.length !== 0)
    ) {
      return invalid;
    }

    const result: CustomerFavouritesProviderResult = {
      ...resultIdentityFromExpectation(expectation),
      status: value.status,
      snapshot,
    };
    return Object.freeze({
      valid: true as const,
      result: cloneAndFreeze(result),
    });
  }

  if (
    value.status === "loading" &&
    hasExactKeys(value, ["status", ...RESULT_IDENTITY_KEYS])
  ) {
    const result: CustomerFavouritesProviderResult = {
      ...resultIdentityFromExpectation(expectation),
      status: "loading",
    };
    return Object.freeze({
      valid: true as const,
      result: cloneAndFreeze(result),
    });
  }

  if (
    value.status === "partial" &&
    hasExactKeys(value, [
      "status",
      ...RESULT_IDENTITY_KEYS,
      "snapshotRevision",
      "reason",
      "missingFields",
      "snapshot",
    ]) &&
    isCustomerFavouritesIdentifier(value.snapshotRevision) &&
    snapshotRevisionMatchesExpectation(value.snapshotRevision, expectation) &&
    value.reason === "provider-marked-partial" &&
    Array.isArray(value.missingFields) &&
    value.missingFields.length > 0 &&
    value.missingFields.every((field) =>
      (CUSTOMER_FAVOURITES_PARTIAL_FIELDS as readonly unknown[]).includes(field),
    ) &&
    new Set(value.missingFields).size === value.missingFields.length
  ) {
    const snapshot = validateCanonicalResultSnapshot(
      value.snapshot,
      expectation,
      catalog,
    );
    if (
      !snapshot ||
      snapshot.snapshotRevision !== value.snapshotRevision ||
      !hasReadOnlyCapabilities(snapshot)
    ) {
      return invalid;
    }

    const result: CustomerFavouritesProviderResult = {
      ...resultIdentityFromExpectation(expectation),
      status: "partial",
      snapshot,
      snapshotRevision: value.snapshotRevision,
      reason: "provider-marked-partial",
      missingFields: value.missingFields as CustomerFavouritesPartialField[],
    };
    return Object.freeze({
      valid: true as const,
      result: cloneAndFreeze(result),
    });
  }

  if (
    value.status === "stale" &&
    hasExactKeys(value, [
      "status",
      ...RESULT_IDENTITY_KEYS,
      "snapshotRevision",
      "reason",
      "snapshot",
    ]) &&
    isCustomerFavouritesIdentifier(value.snapshotRevision) &&
    snapshotRevisionMatchesExpectation(value.snapshotRevision, expectation) &&
    (value.reason === "provider-marked-stale" ||
      value.reason === "snapshot-revision-superseded" ||
      value.reason === "display-revision-superseded")
  ) {
    const snapshot = validateCanonicalResultSnapshot(
      value.snapshot,
      expectation,
      catalog,
    );
    if (
      !snapshot ||
      snapshot.snapshotRevision !== value.snapshotRevision ||
      !hasReadOnlyCapabilities(snapshot)
    ) {
      return invalid;
    }

    const result: CustomerFavouritesProviderResult = {
      ...resultIdentityFromExpectation(expectation),
      status: "stale",
      snapshot,
      snapshotRevision: value.snapshotRevision,
      reason: value.reason,
    };
    return Object.freeze({
      valid: true as const,
      result: cloneAndFreeze(result),
    });
  }

  if (
    value.status === "unavailable" &&
    hasExactKeys(value, ["status", ...RESULT_IDENTITY_KEYS, "reason"]) &&
    value.reason === "favourites-provider-unavailable"
  ) {
    const result: CustomerFavouritesProviderResult = {
      ...resultIdentityFromExpectation(expectation),
      status: "unavailable",
      reason: "favourites-provider-unavailable",
    };
    return Object.freeze({
      valid: true as const,
      result: cloneAndFreeze(result),
    });
  }

  const providerErrorReasons: readonly CustomerFavouritesProviderErrorReason[] = [
    "invalid-request",
    "request-aborted",
    "provider-request-failed",
    "provider-response-invalid",
  ];
  if (
    value.status === "error" &&
    hasExactKeys(value, [
      "status",
      ...RESULT_IDENTITY_KEYS,
      "reason",
      "retryable",
    ]) &&
    providerErrorReasons.includes(
      value.reason as CustomerFavouritesProviderErrorReason,
    ) &&
    typeof value.retryable === "boolean"
  ) {
    const result: CustomerFavouritesProviderResult = {
      ...resultIdentityFromExpectation(expectation),
      status: "error",
      reason: value.reason as CustomerFavouritesProviderErrorReason,
      retryable: value.retryable,
    };
    return Object.freeze({
      valid: true as const,
      result: cloneAndFreeze(result),
    });
  }

  if (
    value.status === "auth-expired" &&
    hasExactKeys(value, ["status", ...RESULT_IDENTITY_KEYS, "reason"]) &&
    value.reason === "customer-session-expired"
  ) {
    const result: CustomerFavouritesProviderResult = {
      ...resultIdentityFromExpectation(expectation),
      status: "auth-expired",
      reason: "customer-session-expired",
    };
    return Object.freeze({
      valid: true as const,
      result: cloneAndFreeze(result),
    });
  }

  if (
    value.status === "customer-mismatch" &&
    hasExactKeys(value, [
      "status",
      ...RESULT_IDENTITY_KEYS,
      "expectedCustomerAccountId",
      "receivedCustomerAccountId",
    ]) &&
    isCustomerFavouritesIdentifier(value.expectedCustomerAccountId) &&
    value.receivedCustomerAccountId === expectation.customerAccountId &&
    value.expectedCustomerAccountId !== value.receivedCustomerAccountId
  ) {
    const result: CustomerFavouritesProviderResult = {
      ...resultIdentityFromExpectation(expectation),
      status: "customer-mismatch",
      expectedCustomerAccountId: value.expectedCustomerAccountId,
      receivedCustomerAccountId: expectation.customerAccountId,
    };
    return Object.freeze({
      valid: true as const,
      result: cloneAndFreeze(result),
    });
  }

  return invalid;
}

export function createCustomerFavouritesProviderResultExpectation(
  provider: Pick<CustomerFavouritesProvider, "providerId" | "providerRevision">,
  request: CustomerFavouritesReadRequest,
): CustomerFavouritesProviderResultExpectation {
  return Object.freeze({
    providerId: provider.providerId,
    providerRevision: provider.providerRevision,
    requestKey: request.requestKey,
    customerAccountId: request.customerAccountId,
    locale: request.locale,
    displayRevision: request.displayRevision,
    snapshotRevision: request.snapshotRevision,
    cursor: request.cursor,
    pageSize: request.pageSize,
    catalog: request.catalog,
  });
}

/**
 * Validates the complete accepted pagination history before another page may
 * become visible. Counts are based on provider references (including exact
 * rejected references), not only renderable items, and cursors/references may
 * never cycle back into an earlier page.
 */
export function customerFavouritesPaginationSequenceIsValid(
  pages: readonly CustomerFavouritesPageProviderResult[],
) {
  if (pages.length === 0) {
    return false;
  }

  const firstPagination = pages[0].snapshot.pagination;
  if (firstPagination.cursor !== null) {
    return false;
  }

  const totalCount = firstPagination.totalCount;
  const priorFavouriteIds = new Set<string>();
  const priorLocationIds = new Set<string>();
  const consumedCursors = new Set<string>();
  let cumulativeReferences = 0;
  let cumulativeItems = 0;
  let cumulativeRejected = 0;

  for (let index = 0; index < pages.length; index += 1) {
    const page = pages[index];
    const snapshot = page.snapshot;
    const pagination = snapshot.pagination;
    const previous = index > 0 ? pages[index - 1] : null;

    if (
      pagination.totalCount !== totalCount ||
      (previous !== null &&
        !customerFavouritesPagesShareLineage(previous, page))
    ) {
      return false;
    }

    if (pagination.cursor !== null) {
      if (consumedCursors.has(pagination.cursor)) {
        return false;
      }
      consumedCursors.add(pagination.cursor);
    }

    if (
      pagination.nextCursor !== null &&
      consumedCursors.has(pagination.nextCursor)
    ) {
      return false;
    }

    if (index > 0) {
      for (const reference of snapshot.favourites) {
        if (
          priorFavouriteIds.has(reference.favouriteId) ||
          priorLocationIds.has(reference.locationId)
        ) {
          return false;
        }
      }
    }

    for (const reference of snapshot.favourites) {
      priorFavouriteIds.add(reference.favouriteId);
      priorLocationIds.add(reference.locationId);
    }

    cumulativeReferences += snapshot.favourites.length;
    cumulativeItems += snapshot.items.length;
    cumulativeRejected += snapshot.rejected.length;

    if (
      pagination.returnedCount !== snapshot.favourites.length ||
      snapshot.items.length + snapshot.rejected.length !==
        snapshot.favourites.length ||
      cumulativeItems + cumulativeRejected !== cumulativeReferences ||
      cumulativeReferences > totalCount ||
      (pagination.nextCursor === null) !==
        (cumulativeReferences === totalCount) ||
      (pagination.nextCursor !== null &&
        pagination.returnedCount !== pagination.pageSize)
    ) {
      return false;
    }
  }

  return true;
}

export function customerFavouritesPagesShareLineage(
  current: CustomerFavouritesDisplayableProviderResult,
  incoming: CustomerFavouritesDisplayableProviderResult,
) {
  return (
    current.snapshot.pagination.nextCursor !== null &&
    current.snapshot.pagination.nextCursor === incoming.cursor &&
    incoming.requestedSnapshotRevision === current.snapshot.snapshotRevision &&
    current.providerId === incoming.providerId &&
    current.providerRevision === incoming.providerRevision &&
    current.customerAccountId === incoming.customerAccountId &&
    current.locale === incoming.locale &&
    current.displayRevision === incoming.displayRevision &&
    current.snapshot.snapshotId === incoming.snapshot.snapshotId &&
    current.snapshot.snapshotRevision === incoming.snapshot.snapshotRevision &&
    current.snapshot.generatedAtEpochMs === incoming.snapshot.generatedAtEpochMs &&
    current.snapshot.pagination.pageSize ===
      incoming.snapshot.pagination.pageSize &&
    current.snapshot.pagination.totalCount ===
      incoming.snapshot.pagination.totalCount &&
    sameCanonicalValue(
      current.snapshot.capabilities,
      incoming.snapshot.capabilities,
    )
  );
}
